import { Box, Button, Flex, Text, Title } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import type { SignupPhoneVerificationRequest } from '../../../../resources/@types/accountSchema';
import type { Captcha } from '../../../hooks/useCaptcha';
import { usePhoneVerificationSignupFeature } from '../../../lib/hackle/feature';
import {
  type AgreementFormValues,
  INITIAL_AGREEMENT_VALUES,
  SignupAgreementSection,
  useIsAgreementValid,
} from '../signup/SignupAgreementSection';
import { PhoneVerificationSection } from './PhoneVerificationSection';

export interface PhoneAndAgreementSubmitHelpers {
  /**
   * 가입 요청이 휴대폰 인증 에러(만료·재사용 등)로 실패했을 때 호출한다. 안내 후 번호 인증을 처음부터 다시 받는다.
   */
  requestPhoneReverification: () => void;
}

interface PhoneAndAgreementStepProps {
  captcha: Captcha;
  isSubmitting: boolean;
  /**
   * 넘기면 이전 단계로 돌아가는 버튼을 보여준다. 이메일 가입(/signup 1단계)에서만 쓴다.
   */
  onBack?: () => void;
  /**
   * phone은 기능플래그 263 ON일 때 인증을 마친 번호이고, OFF면 번호 인증을 받지 않아 null이다.
   */
  onSubmit: (
    values: AgreementFormValues,
    phone: SignupPhoneVerificationRequest | null,
    helpers: PhoneAndAgreementSubmitHelpers,
  ) => void;
}

export function PhoneAndAgreementStep({
  captcha,
  isSubmitting,
  onBack,
  onSubmit,
}: PhoneAndAgreementStepProps) {
  const { t } = useTranslation();
  const [verifiedPhone, setVerifiedPhone] = useState<SignupPhoneVerificationRequest | null>(null);
  // 재인증 시 번호 입력·발송 상태까지 비우려고 인증 섹션을 다시 마운트한다.
  const [phoneSectionKey, setPhoneSectionKey] = useState(0);
  const { isPhoneVerificationSignupFeatureOn } = usePhoneVerificationSignupFeature();
  const isPhoneStepDone = !isPhoneVerificationSignupFeatureOn || verifiedPhone !== null;

  const methods = useForm<AgreementFormValues>({
    defaultValues: INITIAL_AGREEMENT_VALUES,
  });
  const isAgreementValid = useIsAgreementValid(methods.watch);

  const requestPhoneReverification = () => {
    alert(t('phone_verification.signup.reverify_required'));
    setVerifiedPhone(null);
    setPhoneSectionKey((key) => key + 1);
  };

  const handleSubmit = (values: AgreementFormValues) => {
    onSubmit(values, isPhoneVerificationSignupFeatureOn ? verifiedPhone : null, {
      requestPhoneReverification,
    });
  };

  return (
    <FormProvider {...methods}>
      <Flex w="100%" direction="column" justify="center" align="center" mb={24} gap={4}>
        <Title order={2}>{t('서비스 이용 약관')}</Title>
        <Text ta="center">{t('원활한 서비스 이용을 위해 약관에 동의해주세요.')}</Text>
      </Flex>
      {/* 인증 폼들이 각자 <form>이라 약관 <form> 안에 두면 중첩되므로 바깥에 둔다. */}
      {isPhoneVerificationSignupFeatureOn && (
        <Box w="100%" mb={24}>
          <PhoneVerificationSection
            key={phoneSectionKey}
            userType="guest"
            captcha={captcha}
            isVerified={verifiedPhone !== null}
            onVerified={(phone) => setVerifiedPhone(phone)}
          />
        </Box>
      )}
      <Flex
        component="form"
        direction="column"
        w="100%"
        onSubmit={methods.handleSubmit(handleSubmit)}
      >
        <Flex direction="column" gap={24}>
          <SignupAgreementSection showMarketingChannels={isPhoneVerificationSignupFeatureOn} />
          <Button
            type="submit"
            variant="filled"
            color="infgreen"
            size="lg"
            fullWidth
            disabled={!isAgreementValid || !isPhoneStepDone}
            loading={isSubmitting}
          >
            {t('시작하기')}
          </Button>
          {onBack && (
            <Button variant="subtle" color="gray" size="md" fullWidth onClick={onBack}>
              {t('signup.phone_and_agreement.back_button')}
            </Button>
          )}
        </Flex>
      </Flex>
    </FormProvider>
  );
}
